import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]); // For filter dropdown
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  // 🔹 Fetch all products
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const res = await axios.get("https://sonvika.hxbindia.com/api/products");
        const data = res.data.products || [];

        setProducts(data);

        // ✅ Extract unique categories (populated names)
        const uniqueCategories = [
          ...new Set(data.map((p) => p.category?.name).filter(Boolean)),
        ];
        setCategories(uniqueCategories);
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Failed to load products. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // 🔹 Handle product deletion
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    try {
      await axios.delete(`https://sonvika.hxbindia.com/api/products/${id}`);
      setProducts((prev) => prev.filter((p) => p._id !== id));
    } catch (err) {
      console.error("Error deleting product:", err);
      setError("Failed to delete product. Please try again.");
    }
  };

  // 🔹 Apply search + category filter
  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      product.name?.toLowerCase().includes(search.toLowerCase()) ||
      product.description?.toLowerCase().includes(search.toLowerCase()) ||
      product.category?.name?.toLowerCase().includes(search.toLowerCase());

    const matchesCategory =
      selectedCategory === "" || product.category?.name === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  return (
    <div className="container mt-4">
      <h2 className="mb-3">All Products</h2>

      {/* 🔹 Search + Category Filter */}
      <div className="row mb-3 g-2">
        <div className="col-md-6">
          <input
            type="text"
            className="form-control"
            placeholder="Search by name, description, or category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="col-md-6">
          <select
            className="form-select"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map((cat, idx) => (
              <option key={idx} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="text-center py-4">Loading products...</div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-4">No products found.</div>
      ) : (
        <div className="list-group">
          {filteredProducts.map((product) => (
            <div
              key={product._id}
              className="list-group-item d-flex justify-content-between align-items-start flex-wrap"
            >
              {/* Left Side: Image + Info */}
              <div className="d-flex align-items-start flex-wrap">
                {/* 🖼️ Product Image */}
                {product.images && product.images.length > 0 ? (
                  <img
                    src={`https://sonvika.hxbindia.com${product.images[0]}`}
                    alt={product.name}
                    className="img-thumbnail me-3"
                    style={{
                      width: "160px",
                      height: "120px",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <div
                    className="bg-light border d-flex align-items-center justify-content-center me-3"
                    style={{ width: "160px", height: "120px" }}
                  >
                    <span className="text-muted">No image</span>
                  </div>
                )}

                {/* 📋 Product Info */}
                <div style={{ maxWidth: "600px" }}>
                  <h5 className="mb-1">{product.name}</h5>
                  <p className="text-success fw-semibold mb-1">
                    ₹{product.price.toFixed(2)}
                  </p>
                  <p className="mb-1 text-muted">
                    Category: {product.category?.name || "Uncategorized"}
                  </p>
                  {product.description && (
                    <p
                      className="mb-0 text-secondary"
                      style={{ fontSize: "0.9rem" }}
                    >
                      {product.description.length > 120
                        ? product.description.slice(0, 120) + "..."
                        : product.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Right Side: Buttons */}
              <div className="d-flex flex-column align-items-end mt-2 mt-md-0">
                <Link
                  to={`/edit-product/${product._id}`}
                  className="btn btn-warning btn-sm mb-2"
                >
                  Edit
                </Link>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDelete(product._id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductList;
